import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from "../lib/api";
import {
  Clock3,
  Heart,
  MapPin,
  Plus,
  Search,
  Share2,
  SlidersHorizontal,
  Star,
} from 'lucide-react';

const styles = {
  page: {
    maxWidth: 430,
    margin: '0 auto',
    background: '#f2f3f6',
    minHeight: '100vh',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
  },
  hero: {
    position: 'relative',
    height: 240,
    background: '#e4e4e8',
    overflow: 'hidden',
  },
  heroImage: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  backButton: {
    position: 'absolute',
    left: 16,
    top: 16,
    cursor: 'pointer',
    fontSize: '20px',
    background: 'rgba(255,255,255,0.2)',
    padding: '12px',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    transition: 'background 0.2s ease',
  },
  profileCard: {
    margin: '0',
    borderRadius: 0,
    background: '#fff',
    boxShadow: 'none',
    padding: '16px 16px 18px',
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  },
  logo: {
    width: 64,
    height: 64,
    borderRadius: 12,
    objectFit: 'cover',
    background: '#f3f4f6',
    border: '1px solid #ececef',
  },
  profileInfo: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    margin: 0,
    fontSize: 17,
    fontWeight: 700,
    color: '#101113',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  city: {
    margin: '2px 0 0',
    color: '#7a7e86',
    fontSize: 14,
  },
  minOrder: {
    margin: '2px 0 0',
    color: '#7a7e86',
    fontSize: 12.5,
  },
  minOrderPrice: {
    color: 'var(--primary)',
    fontWeight: 700,
  },
  actionButtons: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: 'none',
    background: '#eef1f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    cursor: 'pointer',
  },
  actionButtonActive: {
    background: 'var(--primary)',
    color: '#fff',
  },
  statsRow: {
    margin: '0',
    background: '#f2f3f6',
    boxShadow: 'none',
    padding: '18px 10px 16px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 6,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    marginTop: 3,
  },
  statLabel: {
    fontSize: 12,
    color: '#5e6470',
    marginTop: 2,
  },
  productsSection: {
    marginTop: 0,
    borderTop: '1px solid #e8e9ee',
    background: '#f2f3f6',
    padding: '18px 10px 24px',
  },
  productsHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    padding: '0 6px',
  },
  productsTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: '#111113',
  },
  iconActions: {
    display: 'flex',
    gap: 12,
  },
  iconActionFilled: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: 'none',
    background: '#f1f4f9',
    color: 'var(--primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  iconActionOutline: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: '1.6px solid var(--primary)',
    background: 'transparent',
    color: 'var(--primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  searchWrap: {
    padding: '0 6px 10px',
  },
  searchInput: {
    width: '100%',
    border: '1px solid #d8dce6',
    borderRadius: 12,
    padding: '10px 12px',
    fontSize: 14,
    outline: 'none',
    background: '#fff',
    color: '#16181d',
  },
  filterHint: {
    padding: '0 6px 6px',
    fontSize: 12,
    color: '#646b78',
  },
  categories: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 10,
    marginBottom: 4,
    paddingLeft: 6,
    paddingRight: 6,
  },
  categoryChip: {
    border: 'none',
    background: 'transparent',
    color: '#20232a',
    borderRadius: 14,
    fontSize: 17,
    padding: '10px 0',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  categoryChipActive: {
    background: '#e3e7ee',
    color: 'var(--primary)',
    padding: '10px 16px',
  },
  productList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 8,
    padding: '0 6px',
  },
  productCard: {
    borderRadius: 14,
    border: '1px solid #e8e9ef',
    background: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    padding: 10,
    display: 'flex',
    gap: 12,
    alignItems: 'stretch',
    cursor: 'pointer',
    minHeight: 124,
  },
  productImageWrap: {
    width: 90,
    height: 90,
    borderRadius: 12,
    overflow: 'hidden',
    background: '#f3efe5',
    flexShrink: 0,
    position: 'relative',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  productHeart: {
    position: 'absolute',
    left: 8,
    top: 8,
    color: '#b9bec8',
    background: 'rgba(255,255,255,0.86)',
    borderRadius: '50%',
    width: 26,
    height: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productBody: {
    flex: 1,
    minWidth: 0,
    paddingTop: 2,
  },
  productName: {
    margin: '0 0 4px',
    fontSize: 16,
    fontWeight: 700,
    color: '#141518',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  productUnit: {
    color: '#8d9198',
    fontWeight: 500,
    fontSize: 12,
    marginLeft: 4,
  },
  productPrice: {
    margin: '8px 0 0',
    fontSize: 16.5,
    fontWeight: 700,
    color: '#0d1117',
  },
  addButton: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: '#fff',
    color: 'var(--primary)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    alignSelf: 'flex-end',
    marginBottom: 2,
  },
  emptyText: {
    color: '#7d8290',
    marginTop: 14,
    textAlign: 'center',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 30,
    background: 'rgba(15, 23, 42, 0.48)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 12,
  },
  loading: {
    textAlign: 'center',
    marginTop: 60,
  },
  toast: {
    position: 'fixed',
    bottom: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#111827',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 10,
    fontSize: 13,
    zIndex: 20,
    boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
  },
};

const PharmacyDetailPage = () => {
  const { id } = useParams();
  const [pharmacy, setPharmacy] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('default');
  const [isFavorite, setIsFavorite] = useState(false);
  const [toast, setToast] = useState('');
  const navigate = useNavigate();

  const showToast = (message) => {
    setToast(message);
    window.clearTimeout(window.__pharmacyToastTimer);
    window.__pharmacyToastTimer = window.setTimeout(() => {
      setToast('');
    }, 2000);
  };

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    api.get(`/api/pharmacy/${id}`)
      .then(response => {
        setPharmacy(response.data);
      })
      .catch(error => {
        console.error('Error fetching pharmacy:', error);
      });
  }, [id]);

  useEffect(() => {
    if (!pharmacy?.name) return;

    const fetchMedicines = async () => {
      try {
        const res = await api.get(`api/medicines/pharmacy/${pharmacy.name}`);
        setMedicines(Array.isArray(res.data?.pharma) ? res.data.pharma : []);
      } catch (e) {
        console.log(e);
      }
    };

    fetchMedicines();
  }, [pharmacy]);

  useEffect(() => {
    if (!id) return;
    try {
      const saved = localStorage.getItem(`pharmacy-favorite-${id}`);
      setIsFavorite(saved === 'true');
    } catch (e) {
      setIsFavorite(false);
    }
  }, [id]);

  const toggleFavorite = () => {
    const next = !isFavorite;
    setIsFavorite(next);
    try {
      localStorage.setItem(`pharmacy-favorite-${id}`, String(next));
    } catch (e) {
      // Ignore storage errors and keep UI state.
    }
    showToast(next ? 'Added to favorites' : 'Removed from favorites');
  };

  const handleShare = async () => {
    const shareData = {
      title: pharmacy?.name || 'Pharmacy',
      text: `Check out ${pharmacy?.name || 'this pharmacy'} on Qureo`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        showToast('Shared successfully');
        return;
      }

      await navigator.clipboard.writeText(shareData.url);
      showToast('Link copied');
    } catch (error) {
      showToast('Share cancelled');
    }
  };

  const cycleSortMode = () => {
    const modes = ['default', 'price-low', 'price-high', 'name'];
    const next = modes[(modes.indexOf(sortMode) + 1) % modes.length];
    setSortMode(next);
  };

  const addToCart = async (item) => {
    try {
      await api.post('/api/cart/add', {
        medicineId: item._id,
        dose: item.dosage || '0mg',
        quantity: 1,
      });
      showToast(`${item.name} added to cart`);
    } catch (error) {
      showToast('Failed to add item. Please sign in.');
    }
  };

  const categories = useMemo(() => {
    const unique = Array.from(new Set(medicines.map((m) => m.category).filter(Boolean)));
    return ['All', ...unique];
  }, [medicines]);

  const visibleMedicines = useMemo(() => {
    let list = medicines;

    if (activeCategory !== 'All') {
      list = list.filter((item) => item.category === activeCategory);
    }

    if (searchTerm.trim()) {
      const keyword = searchTerm.trim().toLowerCase();
      list = list.filter((item) => (item.name || '').toLowerCase().includes(keyword));
    }

    const sorted = [...list];
    if (sortMode === 'price-low') {
      sorted.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortMode === 'price-high') {
      sorted.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortMode === 'name') {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    return sorted;
  }, [activeCategory, medicines, searchTerm, sortMode]);

  if (!pharmacy) {
    return <p style={styles.loading}>Loading pharmacy details...</p>;
  }

  const ratingValue = Number(pharmacy.rating || 0).toFixed(1);
  const ratingCount = pharmacy.reviews || 0;
  const locationValue = pharmacy.city || 'Location';
  const deliveryText = pharmacy.deliveryAvailable ? '1-5 hours' : 'Pickup only';
  const coverImage =
    pharmacy.coverImage ||
    pharmacy.banner ||
    pharmacy.image ||
    pharmacy.photo ||
    pharmacy.logo ||
    'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80';

  return (
    <div style={styles.page}>
      <header style={styles.hero}>
        <img src={coverImage} alt={pharmacy.name} style={styles.heroImage} />
        <i
          className="fi fi-br-angle-left"
          onClick={() => navigate(-1)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={styles.backButton}
        ></i>
      </header>

      <section style={styles.profileCard}>
        <img
          src={pharmacy.logo || 'https://via.placeholder.com/120'}
          alt={pharmacy.name}
          style={styles.logo}
        />
        <div style={styles.profileInfo}>
          <h2 style={styles.name}>{pharmacy.name}</h2>
          <p style={styles.city}>{locationValue}</p>
          <p style={styles.minOrder}>
            Minimum Order Amount <span style={styles.minOrderPrice}>$ 0.00</span>
          </p>
        </div>
        <div style={styles.actionButtons}>
          <button
            type="button"
            aria-label="Add to favorites"
            style={{ ...styles.actionButton, ...(isFavorite ? styles.actionButtonActive : {}) }}
            onClick={toggleFavorite}
          >
            <Heart size={23} />
          </button>
          <button type="button" aria-label="Share" style={styles.actionButton} onClick={handleShare}>
            <Share2 size={21} />
          </button>
        </div>
      </section>

      <section style={styles.statsRow}>
        <StatBlock icon={<Star size={18} color="#f59e0b" fill="#f59e0b" />} value={ratingValue} label={`${ratingCount}+ Ratings`} />
        <StatBlock icon={<MapPin size={18} color="var(--primary)" />} value={locationValue} label="Location" />
        <StatBlock icon={<Clock3 size={18} color="var(--primary)" />} value={deliveryText} label="Delivery Time" />
      </section>

      <section style={styles.productsSection}>
        <div style={styles.productsHead}>
          <h3 style={styles.productsTitle}>All Products</h3>
          <div style={styles.iconActions}>
            <button
              type="button"
              aria-label="Search products"
              style={styles.iconActionFilled}
              onClick={() => setSearchOpen((prev) => !prev)}
            >
              <Search size={24} />
            </button>
            <button
              type="button"
              aria-label="Filter products"
              style={styles.iconActionOutline}
              onClick={cycleSortMode}
            >
              <SlidersHorizontal size={24} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div style={styles.searchWrap}>
            <input
              type="text"
              placeholder="Search products"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        )}

        <div style={styles.filterHint}>
          Sort: {sortMode === 'default' ? 'Default' : sortMode === 'price-low' ? 'Price low to high' : sortMode === 'price-high' ? 'Price high to low' : 'Name A-Z'}
        </div>

        <div style={styles.categories}>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              style={{
                ...styles.categoryChip,
                ...(activeCategory === cat ? styles.categoryChipActive : {}),
              }}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div style={styles.productList}>
          {visibleMedicines.map((item) => (
            <ProductRowCard key={item._id || item.id} item={item} onAdd={addToCart} />
          ))}
          {visibleMedicines.length === 0 && (
            <p style={styles.emptyText}>No products available in this category.</p>
          )}
        </div>
      </section>

      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
};

export default PharmacyDetailPage;

const StatBlock = ({ icon, value, label }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'center' }}>{icon}</div>
    <div style={styles.statValue}>{value}</div>
    <div style={styles.statLabel}>{label}</div>
  </div>
);

const ProductRowCard = ({ item, onAdd }) => {
  const navigate = useNavigate();
  const imageUrl = item.images && item.images.length > 0 ? item.images[0] : 'https://via.placeholder.com/140';
  const unitText = item.dosage || item.type || 'Pcs';
  const price = item.price != null ? Number(item.price).toFixed(2) : '--';

  return (
    <div
      style={styles.productCard}
      onClick={() => navigate(`/product/${item._id}`)}
    >
      <div style={styles.productImageWrap}>
        <img src={imageUrl} alt={item.name} style={styles.productImage} />
        <div style={styles.productHeart}>
          <Heart size={16} />
        </div>
      </div>

      <div style={styles.productBody}>
        <h4 style={styles.productName}>
          {item.name} <span style={styles.productUnit}>({unitText})</span>
        </h4>
        <p style={styles.productPrice}>
          $ {price}
        </p>
      </div>

      <button
        type="button"
        style={styles.addButton}
        aria-label={`Add ${item.name}`}
        onClick={async (e) => {
          e.stopPropagation();
          if (item?._id) {
            await onAdd(item);
          }
        }}
      >
        <Plus size={16} />
      </button>
    </div>
  );
};